import { useCallback, useMemo, useState } from "react";
import { z } from "zod";

/**
 * Shared filter state for the contacts list and the leads
 * (inbox) list — lead source, assignee, rooftop, tags, channel, and a
 * date range. The same state shape and serialization drive both lists.
 */

export interface ListFilterState {
  leadSources: string[];
  assigneeIds: string[];
  rooftopIds: string[];
  tagIds: string[];
  channels: string[];
  /** Inclusive `YYYY-MM-DD` bounds; null when unset. */
  dateFrom: string | null;
  dateTo: string | null;
}

export const EMPTY_LIST_FILTERS: ListFilterState = {
  leadSources: [],
  assigneeIds: [],
  rooftopIds: [],
  tagIds: [],
  channels: [],
  dateFrom: null,
  dateTo: null,
};

/** Append the set filters to a query string in the backend's contract. */
export function appendListFilterParams(
  params: URLSearchParams,
  filters: ListFilterState,
): void {
  if (filters.leadSources.length > 0) {
    params.set("leadSource", filters.leadSources.join(","));
  }
  if (filters.assigneeIds.length > 0) {
    params.set("assignee", filters.assigneeIds.join(","));
  }
  if (filters.rooftopIds.length > 0) {
    params.set("rooftop", filters.rooftopIds.join(","));
  }
  if (filters.tagIds.length > 0) params.set("tag", filters.tagIds.join(","));
  if (filters.channels.length > 0) {
    params.set("channel", filters.channels.join(","));
  }
  if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.set("dateTo", filters.dateTo);
}

const LIST_FILTER_PARAM_KEYS = [
  "leadSource",
  "assignee",
  "rooftop",
  "tag",
  "channel",
  "dateFrom",
  "dateTo",
] as const;

function commaSeparated(params: URLSearchParams, key: string): string[] {
  return (params.get(key) ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

/** Parse the canonical shareable filter query. Unknown params are preserved. */
export function readListFilterParams(params: URLSearchParams): ListFilterState {
  const date = (key: string): string | null => {
    const value = params.get(key);
    return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
  };
  return {
    leadSources: commaSeparated(params, "leadSource"),
    assigneeIds: commaSeparated(params, "assignee"),
    rooftopIds: commaSeparated(params, "rooftop"),
    tagIds: commaSeparated(params, "tag"),
    channels: commaSeparated(params, "channel"),
    dateFrom: date("dateFrom"),
    dateTo: date("dateTo"),
  };
}

export function replaceListFilterParams(
  params: URLSearchParams,
  filters: ListFilterState,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of LIST_FILTER_PARAM_KEYS) next.delete(key);
  appendListFilterParams(next, filters);
  next.delete("page");
  return next;
}

/** URL-backed filter state; changing filters never silently changes a default. */
export function useUrlListFilters(
  params: URLSearchParams,
  setParams: (next: URLSearchParams, options?: { replace?: boolean }) => void,
): [ListFilterState, (next: ListFilterState) => void] {
  const filters = useMemo(() => readListFilterParams(params), [params]);
  const setFilters = useCallback(
    (next: ListFilterState) => {
      setParams(replaceListFilterParams(params, next), { replace: true });
    },
    [params, setParams],
  );
  return [filters, setFilters];
}

/** How many distinct filters are currently set — for the "Clear (N)" UI. */
export function activeListFilterCount(filters: ListFilterState): number {
  let count = 0;
  if (filters.leadSources.length > 0) count += 1;
  if (filters.assigneeIds.length > 0) count += 1;
  if (filters.rooftopIds.length > 0) count += 1;
  if (filters.tagIds.length > 0) count += 1;
  if (filters.channels.length > 0) count += 1;
  if (filters.dateFrom || filters.dateTo) count += 1;
  return count;
}

/**
 * Channel filter options. Values are the lead `primary_channel`
 * vocabulary; the backend maps them to `contact_channels` types for the
 * contacts list.
 */
export const CHANNEL_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "sms", label: "SMS / Phone" },
  { value: "messenger", label: "Messenger" },
  { value: "instagram", label: "Instagram" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "telegram", label: "Telegram" },
  { value: "email", label: "Email" },
  { value: "web", label: "Web" },
  { value: "web_form", label: "Web form" },
];

/**
 * Read the persisted filter state for `storageKey` from localStorage,
 * coercing any stale or partial stored shape back to a valid
 * ListFilterState. Returns the empty filters on any miss or error.
 */
export function loadListFilters(storageKey: string): ListFilterState {
  if (typeof window === "undefined") return EMPTY_LIST_FILTERS;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return EMPTY_LIST_FILTERS;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const strings = (v: unknown): string[] =>
      Array.isArray(v)
        ? v.filter((x): x is string => typeof x === "string")
        : [];
    const dateOrNull = (v: unknown): string | null =>
      typeof v === "string" && v.length > 0 ? v : null;
    return {
      leadSources: strings(parsed.leadSources),
      assigneeIds: strings(parsed.assigneeIds),
      rooftopIds: strings(parsed.rooftopIds),
      tagIds: strings(parsed.tagIds),
      channels: strings(parsed.channels),
      dateFrom: dateOrNull(parsed.dateFrom),
      dateTo: dateOrNull(parsed.dateTo),
    };
  } catch {
    return EMPTY_LIST_FILTERS;
  }
}

/** Persist the filter state for `storageKey` to localStorage. */
export function saveListFilters(
  storageKey: string,
  filters: ListFilterState,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(filters));
  } catch {
    // localStorage may be unavailable (private mode, quota); the
    // filters still work for the rest of this session.
  }
}

/**
 * `useState` for list filters, transparently persisted to localStorage
 * under `storageKey` — so the filters a user picks become their
 * default the next time they open that list.
 */
export function usePersistedListFilters(
  storageKey: string,
): [ListFilterState, (next: ListFilterState) => void] {
  const [filters, setFiltersState] = useState<ListFilterState>(() =>
    loadListFilters(storageKey),
  );
  const setFilters = useCallback(
    (next: ListFilterState) => {
      setFiltersState(next);
      saveListFilters(storageKey, next);
    },
    [storageKey],
  );
  return [filters, setFilters];
}

function loadBoolPref(storageKey: string, fallback = false): boolean {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === null ? fallback : stored === "1";
  } catch {
    return fallback;
  }
}

/**
 * Persist the anonymous-contact visibility preference without changing the
 * URL. Collection screens use this when resetting filters and the `anon`
 * query parameter in one atomic navigation, avoiding two competing
 * `setSearchParams` calls built from the same stale snapshot.
 */
export function saveAnonymousFilterPreference(
  storageKey: string,
  value: boolean,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, value ? "1" : "0");
  } catch {
    // localStorage may be unavailable (private mode, quota) — the
    // toggle still works for the rest of this session.
  }
}

/**
 * The shared "show anonymous" quick-filter used by both the inbox and
 * the contacts list. The toggle's last choice is persisted to
 * localStorage under `storageKey` and applied automatically on the
 * next visit; an explicit `?anon` in the URL (a deep link) still wins.
 */
export function usePersistedAnonymousFilter(
  storageKey: string,
  searchParams: URLSearchParams,
  setSearchParams: (
    next: URLSearchParams,
    options?: { replace?: boolean },
  ) => void,
  // Default when the user has neither a `?anon` deep link nor a stored
  // choice. The inbox opts in with `true` (anonymous shown by default,
  // hidden via the "Exclude anonymous" toggle); the contacts list keeps
  // the historical hidden-by-default.
  defaultShow = false,
): [boolean, () => void] {
  const fallback = useMemo(
    () => loadBoolPref(storageKey, defaultShow),
    [storageKey, defaultShow],
  );
  const param = searchParams.get("anon");
  const showAnonymous = param !== null ? param === "1" : fallback;
  const toggle = useCallback(() => {
    const next = !showAnonymous;
    saveAnonymousFilterPreference(storageKey, next);
    const updated = new URLSearchParams(searchParams);
    // Write an explicit value so the URL reflects the choice; the
    // persisted default is only consulted on a fresh visit.
    updated.set("anon", next ? "1" : "0");
    setSearchParams(updated, { replace: true });
  }, [showAnonymous, searchParams, setSearchParams, storageKey]);
  return [showAnonymous, toggle];
}

// ---------------------------------------------------------------------------
// B9 — saved views (localStorage-first per adjudication: NO server table).
// ---------------------------------------------------------------------------

/**
 * One saved inbox view: a named bundle of view tab + filters + sort.
 * Promotion to a `user_saved_views` table + CRUD happens only when
 * cross-device demand is real — until then this rides localStorage
 * beside the one-slot persisted filter above.
 */
export interface SavedListView {
  name: string;
  view: string;
  filters: ListFilterState;
  sort: string;
  position: number;
}

const SAVED_VIEW_VERSION = 1 as const;

const SavedListViewSchema = z.object({
  name: z.string().min(1),
  view: z.string().min(1),
  filters: z.object({
    leadSources: z.array(z.string()),
    assigneeIds: z.array(z.string()),
    rooftopIds: z.array(z.string()),
    tagIds: z.array(z.string()),
    channels: z.array(z.string()),
    dateFrom: z.string().nullable(),
    dateTo: z.string().nullable(),
  }),
  sort: z.string().min(1),
  position: z.number().int().nonnegative(),
});

const SavedListViewsEnvelopeSchema = z.object({
  version: z.literal(SAVED_VIEW_VERSION),
  views: z.array(SavedListViewSchema).max(20),
});

function coerceFilters(value: unknown): ListFilterState {
  const parsed = (value ?? {}) as Record<string, unknown>;
  const strings = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  const dateOrNull = (v: unknown): string | null =>
    typeof v === "string" && v.length > 0 ? v : null;
  return {
    leadSources: strings(parsed.leadSources),
    assigneeIds: strings(parsed.assigneeIds),
    rooftopIds: strings(parsed.rooftopIds),
    tagIds: strings(parsed.tagIds),
    channels: strings(parsed.channels),
    dateFrom: dateOrNull(parsed.dateFrom),
    dateTo: dateOrNull(parsed.dateTo),
  };
}

/**
 * Read the saved views for `storageKey`, coercing stale shapes and
 * re-numbering positions. First-run migration: when no saved-views key
 * exists yet but the one-slot persisted filter (`legacyFilterKey`) has
 * active filters, that filter becomes position 0 ("My filters") — the
 * user's standing setup survives the upgrade instead of demoting to an
 * unnamed slot.
 */
export function loadSavedViews(
  storageKey: string,
  legacyFilterKey?: string,
): SavedListView[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      const envelope = SavedListViewsEnvelopeSchema.safeParse(parsed);
      const storedViews = Array.isArray(parsed)
        ? parsed
        : envelope.success
          ? envelope.data.views
          : [];
      return storedViews
        .filter(
          (v): v is Record<string, unknown> => !!v && typeof v === "object",
        )
        .map((v, i) => ({
          name: typeof v.name === "string" && v.name.trim() ? v.name : `View ${i + 1}`,
          view: typeof v.view === "string" && v.view ? v.view : "needs_you",
          filters: coerceFilters(v.filters),
          sort: typeof v.sort === "string" && v.sort ? v.sort : "recent",
          position: i,
        }))
        .slice(0, 20);
    }
    // Migration path: promote the one-slot filter to position 0 once.
    if (legacyFilterKey) {
      const legacy = loadListFilters(legacyFilterKey);
      if (activeListFilterCount(legacy) > 0) {
        const migrated: SavedListView[] = [
          {
            name: "My filters",
            view: "needs_you",
            filters: legacy,
            sort: "recent",
            position: 0,
          },
        ];
        saveSavedViews(storageKey, migrated);
        return migrated;
      }
    }
    return [];
  } catch {
    return [];
  }
}

/** Persist saved views (positions re-numbered by array order). */
export function saveSavedViews(storageKey: string, views: SavedListView[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        version: SAVED_VIEW_VERSION,
        views: views.map((v, i) => ({ ...v, position: i })),
      }),
    );
  } catch {
    // localStorage may be unavailable (private mode, quota); saved
    // views then last for the session only.
  }
}

/** `useState` for saved views, transparently persisted per user. */
export function useSavedListViews(
  storageKey: string,
  legacyFilterKey?: string,
): [SavedListView[], (next: SavedListView[]) => void] {
  const [views, setViewsState] = useState<SavedListView[]>(() =>
    loadSavedViews(storageKey, legacyFilterKey),
  );
  const setViews = useCallback(
    (next: SavedListView[]) => {
      const renumbered = next.map((v, i) => ({ ...v, position: i }));
      setViewsState(renumbered);
      saveSavedViews(storageKey, renumbered);
    },
    [storageKey],
  );
  return [views, setViews];
}
